/**
 * This class is used to display a basic connectivity error with the web server.
 * These errors can occur when the server is not running or the client is not connected to the internet.
 * Since it's a connectivity error, we can't ask the server for a layout so the UI of this class must be hard-coded.
 */
import { AuthType, GeneralSettings, HorizontalAlignment, StringUtil, VerticalAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { Button, ButtonVariant, HorizontalSpacer, Image, Label, Layout, LayoutProps, Panel } from "..";
import { KeyHandlerGroupRefresher } from "../keyhandling/KeyHandlerGroupRefresher";

export class PanelStaticError extends Layout {
    private buttonClose: Button;
    private _closeCallback: () => void;
    private devDetail: string;
    private errorSummary: string;
    private errorDetail: string;
    private panelButtons: Panel;
    private panelMain: Panel;

    constructor(errorSummary?: string, errorDetail?: string, devDetail?: string, closeCallback?: () => void,
        props?: Partial<LayoutProps>) {
        super({
            auth: AuthType.UNAUTH,
            id: "panelStaticError",
            fillRow: true,
            needsServerLayout: false,
            verticalAlign: VerticalAlignment.CENTER,
            ...props
        });
        this.createPanelMain();
        this.errorSummary = errorSummary;
        this.errorDetail = errorDetail;
        this.devDetail = devDetail;
        this.closeCallback = closeCallback;
    }

    private createPanelMain() {
        this.panelMain = Panel.createNoMarginNoPaddingPanel({
            align: HorizontalAlignment.CENTER,
            fillRow: true,
            id: "panelMain",
        });
    }

    private displayAll() {
        this.removeAll();
        this.panelButtons?.removeAll();
        this.panelMain?.removeAll();
        this.addCloseButton();
        this.addImage();
        this.addErrorSummary();
        this.addErrorDetail();
        this.addDevDetail();
        if (this.panelButtons != null) {
            this.add(this.panelButtons);
        }
        this.add(this.panelMain);
    }

    public get closeCallback(): () => void {
        return this._closeCallback;
    }

    public set closeCallback(value: () => void) {
        this._closeCallback = value;
        this.displayAll();
    }

    private addCloseButton() {
        if (this.closeCallback != null) {
            if (this.panelButtons == null) {
                this.panelButtons = Panel.createNoMarginNoPaddingPanel({ fillRow: true, id: "panelButtons" });
            }
            this.buttonClose = new Button({
                borderWidth: 0,
                cancel: true,
                color: "primary",
                id: "closeButton",
                imageProps: { name: ImageName.x, height: 28, width: 28 },
                onClick: () => this.closeCallback(),
                variant: ButtonVariant.round
            });
            this.buttonClose.addMountListener(() => {
                KeyHandlerGroupRefresher.refresh(this);
            });
            this.panelButtons.add(new HorizontalSpacer(), this.buttonClose);
        }
    }

    private addImage() {
        const image = new Image({
            color: "primary",
            height: 140,
            id: "imageBrokenPallet",
            marginBottom: 48,
            marginTop: this.buttonClose == null ? 64 : 30,
            name: ImageName.brokenPallet,
            rowBreak: true,
            width: 388
        });
        this.panelMain.add(image);
    }

    private addErrorSummary() {
        if (!StringUtil.isEmptyString(this.errorSummary)) {
            const label = new Label({
                color: "primary",
                fontSize: "xxlarge",
                id: "labelErrorSummary",
                text: this.errorSummary
            });
            this.panelMain.add(label);
        }
    }

    private addErrorDetail() {
        if (!StringUtil.isEmptyString(this.errorDetail)) {
            const label = new Label({
                color: "primary",
                fontSize: "large",
                id: "labelErrorDetail",
                text: this.errorDetail
            });
            this.panelMain.add(label);
        }
    }

    private addDevDetail() {
        if (!StringUtil.isEmptyString(this.devDetail) && GeneralSettings.getSingleton()?.isRunningInIDE()) {
            const label = new Label({
                color: "error",
                fontSize: "large",
                id: "labelDevDetail",
                text: this.devDetail
            });
            this.panelMain.add(label);
        }
    }
}
